'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { probeIsStillDown, redirectToHome } from 'src/helper/maintenanceMode';
import { useAppDispatch } from 'src/hooks/redux-hooks';
import { maintenanceModeCleared } from 'src/redux/maintenance/slice';

export const MAINTENANCE_POLL_INTERVAL_MS = 60_000;

/**
 * While maintenance mode is active, checks once a minute whether the backend is
 * back and sends the user home the moment it is.
 *
 * The wait is a CHAINED `setTimeout` rather than a `setInterval`: the probe is a
 * network round trip against a backend that is by definition unhealthy, and
 * `setInterval` would stack a new probe on top of every slow or hanging one.
 * Chaining guarantees at most one probe in flight and a full minute of quiet
 * between them.
 */
export default function useMaintenanceModePoll(isActive: boolean) {
  const dispatch = useAppDispatch();
  const [isChecking, setIsChecking] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Guards the recovery path against a probe that resolves after teardown, and
  // against two probes (timer + manual/refocus) both reporting "back up".
  const isMountedRef = useRef(true);
  const hasRecoveredRef = useRef(false);
  const isProbingRef = useRef(false);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const checkNow = useCallback(async () => {
    // One probe at a time — the timer, the "Check now" button and a tab refocus
    // all land here and must not pile up against a struggling backend.
    if (isProbingRef.current || hasRecoveredRef.current) return;

    isProbingRef.current = true;
    setIsChecking(true);

    const isStillDown = await probeIsStillDown();

    isProbingRef.current = false;
    if (!isMountedRef.current) return;
    setIsChecking(false);

    if (isStillDown) {
      clearTimer();
      timerRef.current = setTimeout(checkNow, MAINTENANCE_POLL_INTERVAL_MS);
      return;
    }

    hasRecoveredRef.current = true;
    clearTimer();
    dispatch(maintenanceModeCleared());
    redirectToHome();
  }, [clearTimer, dispatch]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!isActive) {
      hasRecoveredRef.current = false;
      clearTimer();
      return;
    }

    timerRef.current = setTimeout(checkNow, MAINTENANCE_POLL_INTERVAL_MS);

    // Someone returning to a backgrounded tab shouldn't sit in front of a stale
    // maintenance window for up to a minute, and browsers throttle timers in
    // hidden tabs anyway — so probe immediately on refocus.
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') void checkNow();
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isMountedRef.current = false;
      clearTimer();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isActive, checkNow, clearTimer]);

  return { isChecking, checkNow };
}
